import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { adminService } from '../../../services/adminService';

const AIToolManagementPanel = ({ aiTools, onRefresh }) => {
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', value: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleEdit = (aiTool) => {
    setEditingId(aiTool?.id);
    setFormData({
      name: aiTool?.name || '',
      description: aiTool?.description || '',
      value: aiTool?.value || ''
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({ name: '', description: '', value: '' });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({ name: '', description: '', value: '' });
    setError(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        await adminService?.updateAITool(editingId, formData);
      } else {
        await adminService?.createAITool(formData);
      }

      await onRefresh();
      handleCancel();
    } catch (err) {
      console.error('Error saving AI tool:', err);
      setError(err?.message || 'Failed to save AI tool');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this AI tool? This will also remove all associated relationships.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await adminService?.deleteAITool(id);
      await onRefresh();
    } catch (err) {
      console.error('Error deleting AI tool:', err);
      setError(err?.message || 'Failed to delete AI tool');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">AI Tools</h2>
        <Button
          variant="default"
          size="sm"
          onClick={handleAdd}
          iconName="Plus"
          iconPosition="left"
          disabled={isAdding || editingId}
        >
          Add AI Tool
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive rounded-lg p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Add Form */}
      {isAdding && (
        <div className="mb-6 bg-muted/50 border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Add New AI Tool</h3>
          <div className="space-y-4">
            <Input
              label="Name"
              value={formData?.name}
              onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
              placeholder="e.g., ChatGPT, Claude"
              required
            />
            <Input
              label="Value"
              value={formData?.value}
              onChange={(e) => setFormData({ ...formData, value: e?.target?.value })}
              placeholder="e.g., chatgpt, claude"
              description="Unique identifier (lowercase, no spaces)"
              required
            />
            <Input
              label="Description"
              value={formData?.description}
              onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              placeholder="Brief description of the AI tool"
            />
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                loading={loading}
                disabled={!formData?.name || !formData?.value}
              >
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Tools List */}
      <div className="space-y-3">
        {aiTools?.map((aiTool) => (
          <div
            key={aiTool?.id}
            className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            {editingId === aiTool?.id ? (
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  required
                />
                <Input
                  label="Value"
                  value={formData?.value}
                  onChange={(e) => setFormData({ ...formData, value: e?.target?.value })}
                  description="Unique identifier (lowercase, no spaces)"
                  required
                />
                <Input
                  label="Description"
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                />
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleSave}
                    loading={loading}
                    disabled={!formData?.name || !formData?.value}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-foreground">{aiTool?.name}</h3>
                    <span className="text-xs bg-muted px-2 py-1 rounded">{aiTool?.value}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{aiTool?.description}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="FileText" size={14} />
                    <span>{aiTool?.promptInstructions?.length || 0} prompt instructions</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(aiTool)}
                    iconName="Edit"
                    disabled={loading || isAdding || editingId}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(aiTool?.id)}
                    iconName="Trash2"
                    disabled={loading || isAdding || editingId}
                    className="text-destructive hover:text-destructive"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {aiTools?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Cpu" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No AI tools found. Add your first AI tool to get started.</p>
        </div>
      )}
    </div>
  );
};

export default AIToolManagementPanel;